import { memo, useEffect, useRef } from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';
import { isNoisePreviewLabel } from '../../util/runaiActivity/suppressedMetaTools';
import { createStreamParser, type StreamParser } from '../../util/streaming/streamingMarkdownRenderer';
import { splitStreamingPreviewText } from './splitStreamingPreviewText';

import styles from './StreamingText.module.scss';

type OwnProps = {
  text: string;
  finalText?: string;
  isStreaming: boolean;
  previewText?: string;
};

const StreamingText = ({ text, finalText, isStreaming, previewText }: OwnProps) => {
  const containerRef = useRef<HTMLDivElement>();
  const previewRef = useRef<HTMLDivElement>();
  const parserRef = useRef<StreamParser>();
  const writtenLenRef = useRef(0);

  useEffect(() => {
    return () => {
      parserRef.current?.end();
      parserRef.current = undefined;
      writtenLenRef.current = 0;
    };
  }, []);

  // Track whether we have a non-empty resolved preview so we can suppress the
  // visible previewRow when nothing useful is being streamed. Empty preview
  // rows otherwise leak a generic dot + "Using ..." pill while the v5
  // AssistantInlineExecRows component should be the only status anchor.
  const hasVisiblePreviewRef = useRef(false);

  // Feed new text deltas to parser
  useEffect(() => {
    const el = containerRef.current;
    const previewEl = previewRef.current;
    if (!el) return;

    if (!isStreaming) {
      el.textContent = '';
      if (previewEl) previewEl.textContent = '';

      const finalParser = createStreamParser(el);
      finalParser.write(finalText ?? text);
      finalParser.end();

      parserRef.current = undefined;
      writtenLenRef.current = 0;
      return;
    }

    const split = splitStreamingPreviewText(text);
    const committedText = split.committedText;
    const rawPreviewText = previewText ?? split.previewText;
    // Y1: gateway leaks literal "Using ${toolName}" placeholders (e.g.
    // "Using process") to the preview pill. v5 AssistantInlineExecRows owns
    // the live status surface — drop pure-noise pills here as defense in
    // depth (spec §6a belt-and-suspenders rule).
    const resolvedPreviewText = isNoisePreviewLabel(rawPreviewText)
      ? ''
      : rawPreviewText;

    if (writtenLenRef.current > committedText.length) {
      el.textContent = '';
      parserRef.current?.end();
      parserRef.current = undefined;
      writtenLenRef.current = 0;
    }

    if (!parserRef.current) {
      parserRef.current = createStreamParser(el);
      writtenLenRef.current = 0;
    }

    const newText = committedText.slice(writtenLenRef.current);
    if (newText) {
      parserRef.current.write(newText);
      writtenLenRef.current = committedText.length;
    }

    if (previewEl) previewEl.textContent = resolvedPreviewText ?? '';
    hasVisiblePreviewRef.current = Boolean(resolvedPreviewText && resolvedPreviewText.trim().length);
  }, [finalText, previewText, text, isStreaming]);

  // Compute initial render visibility for the preview row. We render the row
  // container always so the ref attaches (effect needs it), but hide it with
  // CSS until a non-empty, non-noise preview arrives.
  const initialRawPreview = previewText ?? splitStreamingPreviewText(text).previewText;
  const initialPreview = isNoisePreviewLabel(initialRawPreview) ? '' : initialRawPreview;
  const showPreviewRow = isStreaming && Boolean(initialPreview && initialPreview.trim().length);

  return (
    <div className={buildClassName(styles.root, !isStreaming && styles.done)}>
      <div ref={containerRef} className={styles.content} />
      {isStreaming && (
        <div
          className={styles.previewRow}
          style={showPreviewRow ? undefined : { display: 'none' }}
        >
          <span className={styles.previewDot}>●</span>
          <div ref={previewRef} className={styles.previewText} />
        </div>
      )}
    </div>
  );
};

export default memo(StreamingText);
